import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useLazyQuery, useLazyCommand, useEvent, useApiErrors } from '@soap/modules';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { UnarchiveStudentDialog } from '../components/dialogs/UnarchiveStudentDialog';
import {
  emptyStudentDateOfBirth,
  StudentDateOfBirthFields,
  StudentDateOfBirthValue,
  toAddStudentDateOfBirthCommand,
} from '../components/StudentDateOfBirthFields';

interface Tag {
  id: string;
  title: string;
}

interface CreateTagDialogProps {
  onClose: () => void;
  onCreate: (tagName: string, tagDescription: string) => void;
}

function CreateTagDialog({ onClose, onCreate }: CreateTagDialogProps) {
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tagName.trim()) {
      onCreate(tagName.trim(), tagDescription.trim());
      setTagName('');
      setTagDescription('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle>Create New Tag</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tagName">Tag Name</Label>
              <Input
                id="tagName"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                required
                placeholder="Enter tag name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagDescription">Description (Optional)</Label>
              <Input
                id="tagDescription"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Enter description"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Create Tag</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function AddStudentPage() {
  const viewStates = {
    loading: 0,
    ready: 1,
    submitting: 2
  };

  const [viewState, setViewState] = useState(viewStates.loading);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [showUnarchiveStudentDialog, setShowUnarchiveStudentDialog] = useState(false);
  const [unarchiveStudentId, setUnarchiveStudentId] = useState<string | null>(null);
  const [unarchiveStudentName, setUnarchiveStudentName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<StudentDateOfBirthValue>(emptyStudentDateOfBirth());

  const [getFormData] = useLazyQuery('C160v1_GetC159FormData', {});
  const [sendCommand] = useLazyCommand('C159v1_AddStudent');
  const [createTagCommand] = useLazyCommand('C120v1_AddTag');
  const { errors } = useApiErrors();

  // Listen for form data event
  useEvent({
    eventName: "E132v1_GotC159FormData",
    onEventReceived: (event: any) => {
      const fieldData = event.e000_FieldData || [];
      const tagsField = fieldData.find((field: any) => field.name === 'c159_Tags');
      
      if (tagsField && tagsField.initialValue) {
        const tagData = tagsField.initialValue;
        const allEnumerations = tagData.AllEnumerations || tagData.allEnumerations || [];
        
        const allTags: Tag[] = allEnumerations.map((t: any) => ({
          id: t.Key || t.key,
          title: t.Value || t.value
        }));
        
        setAvailableTags(allTags);
        setViewState(viewStates.ready);
      } else {
        setViewState(viewStates.ready);
      }
    }
  });

  // Listen for successful add event
  useEvent({
    eventName: "E133v1_StudentAdded",
    onEventReceived: () => {
      location.href = '#/app/profiles/students';
    }
  });

  // Listen for tag added event
  useEvent({
    eventName: "E105v1_TagAdded",
    onEventReceived: (event: any) => {
      if (event.e105_TagTypeKey === "student") {
        getFormData({});
      }
    }
  }, [getFormData]);

  // Listen for email reuse requires unarchive event
  useEvent({
    eventName: "E203v1_EmailReuseRequiresUnarchive",
    onEventReceived: (event: any) => {
      const errorCode = event.e203_ErrorCode;
      if (errorCode === "EmailReuseRequiresUnarchiveStudent" && viewState === viewStates.submitting) {
        setUnarchiveStudentId(event.e203_StudentId);
        // Extract student name from message if available, or use a default
        const message = event.e203_Message || '';
        const nameMatch = message.match(/\(([^)]+)\)/);
        setUnarchiveStudentName(nameMatch ? nameMatch[1] : 'this student');
        setShowUnarchiveStudentDialog(true);
        setViewState(viewStates.ready);
      }
    }
  });

  // Reset submitting state when an error occurs
  useEffect(() => {
    if (errors.length > 0 && viewState === viewStates.submitting) {
      setViewState(viewStates.ready);
    }
  }, [errors, viewState]);

  const handleTagToggle = (tag: Tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    
    if (isSelected) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleCreateTag = async (tagName: string, tagDescription: string) => {
    createTagCommand({
      $type: "C120v1_AddTag",
      C120_TagName: tagName,
      C120_TagDescription: tagDescription,
      C120_TagTypeKey: "student"
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateOfBirth.month || !dateOfBirth.year) {
      return;
    }
    setViewState(viewStates.submitting);

    await sendCommand({
      C159_FirstName: firstName,
      C159_LastName: lastName,
      C159_Email: email,
      C159_DateOfBirth: toAddStudentDateOfBirthCommand(dateOfBirth),
      C159_Tags: {
        SelectedKeys: selectedTags.map(t => t.id)
      }
    });
  };

  switch (viewState) {
    case viewStates.loading:
      return (
        <div className="p-4 sm:p-6">
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                <span className="text-gray-600">Loading...</span>
              </div>
            </div>
          </div>
      );

    case viewStates.submitting:
      return (
        <div className="p-4 sm:p-6">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-6">
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                    <span className="text-gray-600">Adding student...</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
      );

    case viewStates.ready:
      return (
        <div className="p-4 sm:p-6">
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => location.href = '#/app/profiles/students'}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <CardTitle className="text-2xl">Add Student</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="text-sm text-gray-500">
                      Email is optional. Students without emails can still receive assessments via shareable links.
                    </p>
                  </div>

                  <StudentDateOfBirthFields
                    idPrefix="add-student-dob"
                    value={dateOfBirth}
                    onChange={setDateOfBirth}
                  />

                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <p className="text-sm text-gray-600 mb-4">
                      Select tags to categorize and organize this student.
                    </p>
                    
                    {/* Selected Tags */}
                    {selectedTags.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Selected Tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTags.map(tag => (
                            <Badge
                              key={tag.id}
                              variant="secondary"
                              className="inline-flex items-center gap-1 px-3 py-1"
                            >
                              {tag.title}
                              <button
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className="hover:bg-gray-300 rounded-full p-0.5"
                              >
                                <X size={14} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available Tags */}
                    {availableTags.length > 0 ? (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">Available Tags:</p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setShowCreateTag(true)}
                            className="text-xs"
                          >
                            <Plus size={14} className="mr-1" />
                            Create New Tag
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {availableTags
                            .filter(tag => !selectedTags.some(t => t.id === tag.id))
                            .map(tag => (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => handleTagToggle(tag)}
                                className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
                              >
                                {tag.title}
                              </button>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-3">No tags available yet.</p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowCreateTag(true)}
                        >
                          <Plus size={16} className="mr-2" />
                          Create Your First Tag
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => location.href = '#/app/profiles/students'}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={!dateOfBirth.month || !dateOfBirth.year}>Add Student</Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {showCreateTag && (
              <CreateTagDialog
                onClose={() => setShowCreateTag(false)}
                onCreate={handleCreateTag}
              />
            )}

            {showUnarchiveStudentDialog && unarchiveStudentId && (
              <UnarchiveStudentDialog
                studentId={unarchiveStudentId}
                studentName={unarchiveStudentName}
                onClose={() => {
                  setShowUnarchiveStudentDialog(false);
                  setUnarchiveStudentId(null);
                  setUnarchiveStudentName('');
                }}
              />
            )}
          </div>
      );
  }
}

